import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-hot-toast";

const headerStyle = {
  color: "white",
  fontWeight: 600,
  fontSize: "14px",
  padding: "16px 20px",
  borderBottom: "2px solid #000080",
  position: "sticky",
  top: 0,
  zIndex: 2,
  backgroundColor: "#bb1ccc !important",
};

const cellStyle = {
  padding: "16px 20px",
  fontSize: "14px",
};

const getPathPlaceholder = () => "e.g. catalog.schema";
const getExternalLocationPlaceholder = () =>
  "e.g. abfss://container@storage.dfs.core.windows.net/path";

export default function ErrorSchemaSection({ productId }) {
  const [isProductLevel, setIsProductLevel] = useState(null);

  const [productType, setProductType] = useState("");
  const [productPath, setProductPath] = useState("");
  const [productAdlsLocation, setProductAdlsLocation] = useState("");
  const [assetRows, setAssetRows] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const apiDataRef = useRef(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoadingAssets(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/errorCatalog/${productId}`)
      .then((res) => {
        apiDataRef.current = res.data || {};
      })
      .catch(() => {
        hasFetched.current = false;
        toast.error("Failed to load error schema configuration.");
      })
      .finally(() => setLoadingAssets(false));
  }, [productId]);

  const handleProductLevelChange = (value) => {
    setIsProductLevel(value);

    const cached = apiDataRef.current;

    if (value === true) {
      setProductType(cached?.productLevelType || "");
      setProductPath(cached?.productLevelErrorSchema || "");
      setProductAdlsLocation(cached?.productLevelAdlsLocation || "");
      setAssetRows([]);
    } else {
      const rows = (cached?.assets || []).map((a) => ({
        assetId: a.assetId,
        assetName: a.assetName,
        type: a.type || "",
        path: a.errorSchema || "",
        adlsLocation: a.adlsLocation || "",
      }));
      setAssetRows(rows);
      setProductType("");
      setProductPath("");
      setProductAdlsLocation("");
    }
  };

  const handleProductTypeClick = (clickedValue) => {
    if (productType === clickedValue) {
      setProductType("");
      setProductPath("");
      setProductAdlsLocation("");
    }
  };

  const handleAssetTypeChange = (assetId, value) => {
    setAssetRows((prev) =>
      prev.map((r) => (r.assetId === assetId ? { ...r, type: value } : r))
    );
  };

  const handleAssetTypeClick = (assetId, clickedValue) => {
    setAssetRows((prev) =>
      prev.map((r) => {
        if (r.assetId !== assetId) return r;
        return r.type === clickedValue
          ? { ...r, type: "", path: "", adlsLocation: "" }
          : r;
      })
    );
  };

  const handleAssetPathChange = (assetId, value) => {
    setAssetRows((prev) =>
      prev.map((r) => (r.assetId === assetId ? { ...r, path: value } : r))
    );
  };

  const handleAssetAdlsLocationChange = (assetId, value) => {
    setAssetRows((prev) =>
      prev.map((r) => (r.assetId === assetId ? { ...r, adlsLocation: value } : r))
    );
  };

  const isApplyEnabled = (() => {
    if (isProductLevel === null) return false;
    if (isProductLevel === true) {
      if (!productType) return false;
      if (productType === "uc")
        return productPath.trim() !== "" && productAdlsLocation.trim() !== "";
      // adls: only external location required
      return productAdlsLocation.trim() !== "";
    }
    if (assetRows.length === 0) return false;
    return assetRows.every((r) => {
      const hasType = r.type !== "";
      const hasPath = r.path.trim() !== "";
      const hasAdls = r.adlsLocation.trim() !== "";
      // fully empty row is valid (optional)
      if (!hasType && !hasPath && !hasAdls) return true;
      if (r.type === "uc") return hasPath && hasAdls;
      if (r.type === "adls") return hasAdls;
      return false;
    });
  })();

  const handleApplyChanges = async () => {
    try {
      setSubmitting(true);
      const payload = {
        isProductLevel,
        productId,
        productLevelErrorSchema: isProductLevel
          ? {
              productLevelType: productType,
              productLevelErrorSchema: productType === "uc" ? productPath : null,
              productLevelAdlsLocation: productAdlsLocation,
            }
          : null,
        assetLevelErrorSchema: !isProductLevel
          ? assetRows.map((r) => ({
              assetId: r.assetId,
              assetName: r.assetName,
              type: r.type,
              errorSchema: r.type === "uc" ? r.path : null,
              adlsLocation: r.adlsLocation,
            }))
          : [],
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/errorCatalog/${productId}`,
        payload
      );
      toast.success(data?.message || "Error schema configuration saved successfully!");
    } catch {
      toast.error("Failed to save error schema. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const applyButton = (tooltipMsg) => (
    <Tooltip title={!isApplyEnabled ? tooltipMsg : ""} placement="top" arrow>
      <span>
        <Button
          variant="contained"
          disabled={!isApplyEnabled || submitting}
          onClick={handleApplyChanges}
          sx={{
            backgroundColor: isApplyEnabled ? "#bb1ccc" : undefined,
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            height: "40px",
            "&:hover": { backgroundColor: "#000080" },
            "&.Mui-disabled": { backgroundColor: "#d1d5db", color: "#9ca3af" },
          }}
        >
          {submitting ? "Saving..." : "Apply Changes"}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
          padding: "20px 24px",
          mb: 3,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1a1a2e", mb: 2 }}>
          Do you need to store errors at Product Level?
        </Typography>

        <RadioGroup
          row
          value={isProductLevel === null ? "" : isProductLevel ? "yes" : "no"}
          onChange={(e) => handleProductLevelChange(e.target.value === "yes")}
        >
          <FormControlLabel
            value="yes"
            control={<Radio sx={{ color: "#bb1ccc", "&.Mui-checked": { color: "#bb1ccc" } }} />}
            label={<Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Yes</Typography>}
          />
          <FormControlLabel
            value="no"
            control={<Radio sx={{ color: "#bb1ccc", "&.Mui-checked": { color: "#bb1ccc" } }} />}
            label={<Typography sx={{ fontWeight: 600, fontSize: "14px" }}>No</Typography>}
          />
        </RadioGroup>

        {isProductLevel === true && (
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#555", mb: 0.5 }}>
                Type
              </Typography>
              <RadioGroup
                row
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
              >
                {["uc", "adls"].map((typeValue) => (
                  <FormControlLabel
                    key={typeValue}
                    value={typeValue}
                    control={
                      <Radio
                        size="small"
                        sx={{ color: "#bb1ccc", "&.Mui-checked": { color: "#bb1ccc" } }}
                        onClick={() => handleProductTypeClick(typeValue)}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                        {typeValue.toUpperCase()}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>

            {productType === "uc" && (
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#555", mb: 0.5 }}>
                  Path
                </Typography>
                <TextField
                  size="small"
                  placeholder={getPathPlaceholder()}
                  value={productPath}
                  onChange={(e) => setProductPath(e.target.value)}
                  sx={{
                    width: "480px",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "#bb1ccc" },
                      "&.Mui-focused fieldset": { borderColor: "#bb1ccc" },
                    },
                  }}
                />
              </Box>
            )}

            {productType && (
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#555", mb: 0.5 }}>
                  External Location
                </Typography>
                <TextField
                  size="small"
                  placeholder={getExternalLocationPlaceholder()}
                  value={productAdlsLocation}
                  onChange={(e) => setProductAdlsLocation(e.target.value)}
                  sx={{
                    width: "480px",
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": { borderColor: "#bb1ccc" },
                      "&.Mui-focused fieldset": { borderColor: "#bb1ccc" },
                    },
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              {applyButton(
                productType === "uc"
                  ? "Please fill in the path and external location"
                  : "Please select a type and fill in the external location"
              )}
            </Box>
          </Box>
        )}
      </Box>

      {isProductLevel === false && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#1a1a2e" }}>
              Asset Error Schema Configuration
            </Typography>
            {applyButton("Fill in required fields for each asset")}
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              maxHeight: "500px",
              overflow: "auto",
            }}
          >
            <Table sx={{ minWidth: 800 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerStyle, width: "28%" }}>Asset Name</TableCell>
                  <TableCell sx={{ ...headerStyle, width: "18%" }}>Type</TableCell>
                  <TableCell sx={{ ...headerStyle, width: "24%" }}>Path</TableCell>
                  <TableCell sx={{ ...headerStyle, width: "30%" }}>External Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingAssets ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                      Loading assets...
                    </TableCell>
                  </TableRow>
                ) : assetRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                      No assets found.
                    </TableCell>
                  </TableRow>
                ) : (
                  assetRows.map((row, index) => (
                    <TableRow
                      key={row.assetId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f0f8ff",
                          transition: "background-color 0.2s ease",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          ...cellStyle,
                          fontWeight: 600,
                          color: "#2c3e50",
                          width: "28%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {row.assetName}
                      </TableCell>

                      <TableCell sx={{ ...cellStyle, width: "18%" }}>
                        <RadioGroup
                          row
                          value={row.type}
                          onChange={(e) => handleAssetTypeChange(row.assetId, e.target.value)}
                        >
                          {["uc", "adls"].map((typeValue) => (
                            <FormControlLabel
                              key={typeValue}
                              value={typeValue}
                              control={
                                <Radio
                                  size="small"
                                  sx={{ color: "#bb1ccc", "&.Mui-checked": { color: "#bb1ccc" } }}
                                  onClick={() => handleAssetTypeClick(row.assetId, typeValue)}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                  {typeValue.toUpperCase()}
                                </Typography>
                              }
                            />
                          ))}
                        </RadioGroup>
                      </TableCell>

                      <TableCell
                        sx={{
                          ...cellStyle,
                          width: "24%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        <TextField
                          size="small"
                          value={row.path}
                          disabled={!row.type || row.type === "adls"}
                          onChange={(e) => handleAssetPathChange(row.assetId, e.target.value)}
                          placeholder={
                            row.type === "uc"
                              ? getPathPlaceholder()
                              : row.type === "adls"
                              ? "N/A for ADLS"
                              : "Select a type first"
                          }
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#bb1ccc" },
                              "&.Mui-focused fieldset": { borderColor: "#bb1ccc" },
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          ...cellStyle,
                          width: "30%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        <TextField
                          size="small"
                          value={row.adlsLocation}
                          disabled={!row.type}
                          onChange={(e) =>
                            handleAssetAdlsLocationChange(row.assetId, e.target.value)
                          }
                          placeholder={
                            row.type
                              ? getExternalLocationPlaceholder()
                              : "Select a type first"
                          }
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": { borderColor: "#bb1ccc" },
                              "&.Mui-focused fieldset": { borderColor: "#bb1ccc" },
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
